import { Box, Checkbox, Link, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import * as R from "ramda";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
  content: string;
  variant?: "default" | "compact";
}

const StyledTableContainer = styled(TableContainer)({
  marginBottom: "16px",
  "& table": {
    minWidth: "auto",
    borderCollapse: "collapse"
  },
  "& th, & td": {
    padding: "8px 16px",
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    textAlign: "left"
  },
  "& th": {
    fontWeight: "bold"
  }
});

const HighlightedText = styled("span")(({ theme }) => ({
  backgroundColor: theme.palette.warning.light,
  padding: "0 4px",
  borderRadius: "2px"
}));

const ImageWrapper = styled(Box)({
  maxWidth: "100%",
  margin: "16px 0",
  "& img": {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "4px"
  }
});

const CodeBlock = styled(Box)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflow: "auto",
  marginBottom: theme.spacing(2),
  fontSize: "0.875rem",
  fontFamily: "monospace"
}));

export const MarkdownRenderer = ({ content, variant = "default" }: MarkdownRendererProps) => {
  const isCompact = variant === "compact";

  // Define all component renderers
  const renderers = {
    h1: (props: any) => (
      <Typography
        variant={isCompact ? "subtitle1" : "h6"}
        fontWeight="bold"
        gutterBottom
        {...props}
      />
    ),
    h2: (props: any) => (
      <Typography
        variant={isCompact ? "subtitle2" : "subtitle1"}
        fontWeight="bold"
        gutterBottom
        {...props}
      />
    ),
    h3: (props: any) => (
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        gutterBottom
        {...props}
      />
    ),
    p: (props: any) => (
      <Typography
        variant="body2"
        paragraph
        {...props}
      />
    ),
    ul: (props: any) => (
      <Box
        component="ul"
        sx={{ pl: 2, mb: 2 }}
        {...props}
      />
    ),
    ol: (props: any) => (
      <Box
        component="ol"
        sx={{ pl: 2, mb: 2 }}
        {...props}
      />
    ),
    li: ({ checked, children, ...props }: any) => R.cond([
      // Regular list item
      [
        () => R.isNil(checked),
        () => (
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 0.5 }}
            {...props}
          >
            {children}
          </Typography>
        )
      ],
      // Task list item (with checkbox)
      [
        R.T,
        () => (
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 0.5, display: "flex", alignItems: "center" }}
            {...props}
          >
            <Checkbox
              checked={checked}
              disabled
              size="small"
              sx={{ mr: 1, p: 0.5 }}
            />
            <span>{children}</span>
          </Typography>
        )
      ]
    ])(),
    a: (props: any) => (
      <Link
        href={props.href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      />
    ),
    // Table rendering
    table: ({ children, ...props }: any) => (
      <StyledTableContainer>
        <Paper variant="outlined">
          <Table size="small" {...props}>
            {children}
          </Table>
        </Paper>
      </StyledTableContainer>
    ),
    thead: ({ children, ...props }: any) => (
      <TableHead {...props}>
        {children}
      </TableHead>
    ),
    tbody: ({ children, ...props }: any) => (
      <TableBody {...props}>
        {children}
      </TableBody>
    ),
    tr: ({ children, ...props }: any) => (
      <TableRow {...props}>
        {children}
      </TableRow>
    ),
    th: ({ children, ...props }: any) => (
      <TableCell component="th" {...props}>
        <Typography variant="subtitle2" fontWeight="bold">
          {children}
        </Typography>
      </TableCell>
    ),
    td: ({ children, ...props }: any) => (
      <TableCell {...props}>
        <Typography variant="body2">
          {children}
        </Typography>
      </TableCell>
    ),
    // Highlight support
    mark: ({ children, ...props }: any) => (
      <HighlightedText {...props}>
        {children}
      </HighlightedText>
    ),
    // Image support
    img: ({ src, alt, ...props }: any) => (
      <ImageWrapper>
        <img src={src} alt={alt || ""} {...props} />
      </ImageWrapper>
    ),
    // Code blocks
    pre: (props: any) => (
      <CodeBlock
        component="pre"
        {...props}
      />
    )
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={renderers}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
